/*
  ADH buying panel (catalog-wide product-page chooser).
  Progressive enhancement over the native Kalles product form: the panel never
  adds to cart itself. It only sets the form's own variant select, so Shopify's
  variant IDs, prices, inventory rules, cart and checkout stay authoritative.
  If anything needed is missing, the panel stays hidden and the page keeps the
  theme's original controls (data-adh-state records why).
*/
(function () {
  'use strict';

  var VERSION = '2026-10-26.2';

  // Listings whose quantity values do not state the real unit count, so no per-unit
  // price or unit total is shown (found by the Oct 5 final review; product data unchanged):
  // 7272173633595 File Right Number Labels Roll Set: values read "500 Per Roll" but one pack is a 10-roll set.
  // 7272182710331 File Right Number Labels Ringbook Set: values read "500 Per Roll" but it is 10 books of 240 labels.
  var UNITS_UNKNOWN = { '7272173633595': true, '7272182710331': true };
  var PACKAGE_WORDS = 'roll|pack|box|book|pad|set|case|bag';
  var N = '(\\d[\\d,]*)';

  function num(text) { return Number(String(text).replace(/,/g, '')); }
  function fmt(n) { return Number(n).toLocaleString('en-US'); }
  function clean(text) { return String(text == null ? '' : text).replace(/\s+/g, ' ').trim(); }

  function singular(word) {
    var w = clean(word).toLowerCase();
    if (!w) return '';
    if (w === 'pcs' || w === 'pc') return 'piece';
    if (/(box|bus|ch|sh)es$/.test(w)) return w.slice(0, -2);
    if (/[^aeiou]ies$/.test(w)) return w.slice(0, -3) + 'y';
    if (/s$/.test(w) && !/ss$/.test(w)) return w.slice(0, -1);
    return w;
  }
  function plural(word) {
    var w = singular(word);
    if (!w) return '';
    if (/(x|s|ch|sh)$/.test(w)) return w + 'es';
    if (/[^aeiou]y$/.test(w)) return w.slice(0, -1) + 'ies';
    return w + 's';
  }
  function nounFor(word, count) { return count === 1 ? singular(word) : plural(word); }
  function sane(n) { return isFinite(n) && n >= 1 && n <= 100000 && Math.floor(n) === n; }

  // Packaged quantity: "packages" containers each holding "per" units (total = packages × per).
  function packaged(packages, pkgWord, per, unitWord, value) {
    if (!sane(packages) || !sane(per)) return null;
    var pkg = singular(pkgWord);
    var unit = singular(unitWord);
    var total = packages * per;
    if (per === 1 && !unit) {
      // "4 x 1 Per Set" is simply four sets.
      return { value: value, count: packages, noun: pkg, heading: fmt(packages) + ' ' + nounFor(pkg, packages), detail: '' };
    }
    var unitsLabel = unit ? ' ' + nounFor(unit, total) : ' total';
    return {
      value: value,
      count: total,
      noun: unit,
      packages: packages,
      pkg: pkg,
      per: per,
      heading: fmt(packages) + ' ' + nounFor(pkg, packages) + ' · ' + fmt(total) + unitsLabel,
      detail: fmt(per) + (unit ? ' ' + nounFor(unit, per) : '') + ' per ' + pkg
    };
  }
  // Plain count: the value itself names how many units are bought.
  function counted(count, unitWord, value, detail) {
    if (!sane(count)) return null;
    return { value: value, count: count, noun: singular(unitWord || ''), heading: value, detail: detail || '' };
  }

  /*
    Parse an option value into a quantity description. Returns null when the
    value is not a quantity. A result with count === null is still treated as a
    quantity choice, but no per-unit price is shown because the total is not
    stated unambiguously in the value text.
  */
  function parseQuantity(raw, optionName) {
    var v = clean(raw);
    var m;
    var re = function (src) { return new RegExp('^' + src + '$', 'i'); };

    // Roll of 2 (800 Labels) -> 2 rolls holding 800 labels in total (400 per roll).
    if ((m = v.match(re('(' + PACKAGE_WORDS + ')s? ?of ' + N + ' \\(' + N + ' ([a-z][a-z ]*?)\\)')))) {
      var pk = num(m[2]), tot = num(m[3]);
      if (pk > 0 && tot % pk === 0) return packaged(pk, m[1], tot / pk, m[4], v);
      return null;
    }
    // 10 Pads (50 Sheets per Pad) / 1 Pad (100 Forms/Pad)
    if ((m = v.match(re(N + ' (' + PACKAGE_WORDS + ')(?:e?s)? \\(' + N + ' ([a-z][a-z ]*?)(?: per |\\/)(' + PACKAGE_WORDS + ')\\)')))) {
      return packaged(num(m[1]), m[2], num(m[3]), m[4], v);
    }
    // Pad 1 (100 Sheets/Pad) / Book 5 (50 Forms/Book)
    if ((m = v.match(re('(' + PACKAGE_WORDS + ') ' + N + ' \\(' + N + ' ([a-z][a-z ]*?)(?: per |\\/)(' + PACKAGE_WORDS + ')\\)')))) {
      return packaged(num(m[2]), m[1], num(m[3]), m[4], v);
    }
    // 2 Books (50 Forms) / 2 Set (8 Pieces) -> total stated, per-package derived
    if ((m = v.match(re(N + ' (' + PACKAGE_WORDS + ')(?:e?s)? \\(' + N + ' ([a-z][a-z ]*?)\\)')))) {
      var p2 = num(m[1]), t2 = num(m[3]);
      if (p2 > 0 && t2 % p2 === 0) return packaged(p2, m[2], t2 / p2, m[4], v);
      return null;
    }
    // 2 x 500 Year Labels Per Roll / 3 x 1000 Per Set
    if ((m = v.match(re(N + ' ?x ?' + N + '(?: ([a-z][a-z ]*?))? per (' + PACKAGE_WORDS + ')')))) {
      return packaged(num(m[1]), m[4], num(m[2]), m[3] || '', v);
    }
    // Pack of 1000/Pack of 4 Rolls -> 1,000 in total across 4 rolls
    if ((m = v.match(re('pack ?of ' + N + ' ?\\/ ?pack ?of ' + N + ' (' + PACKAGE_WORDS + '|boxes|rolls)')))) {
      var t3 = num(m[1]), p3 = num(m[2]);
      if (p3 > 0 && t3 % p3 === 0) return packaged(p3, m[3], t3 / p3, '', v);
      return null;
    }
    // Set of 2 - 540 Labels -> 2 sets holding 540 labels in total
    if ((m = v.match(re('(' + PACKAGE_WORDS + ')s? ?of ' + N + ' - ' + N + ' ([a-z][a-z ]*?)')))) {
      var p4 = num(m[2]), t4 = num(m[3]);
      if (p4 > 0 && t4 % p4 === 0) return packaged(p4, m[1], t4 / p4, m[4], v);
      return null;
    }
    // 500 Per Roll / 1250 Per Box / 30 per tub -> one container of N
    if ((m = v.match(re(N + ' per (' + PACKAGE_WORDS + '|tub)')))) {
      return counted(num(m[1]), '', v, '');
    }
    // Pack of 100 - Shrink-Wrapped
    if ((m = v.match(re('pack ?of ' + N + ' - (.+)')))) {
      return counted(num(m[1]), '', v, '');
    }
    // Pack of 12 Pads / Pack of 1000 Tags / Pack of 2 Pound
    if ((m = v.match(re('pack ?of ' + N + ' ([a-z]+)')))) {
      return counted(num(m[1]), m[2], v, '');
    }
    // Pack of 12 / Box of 500 / Roll of 500 / Book of 4 / Set of 3
    if ((m = v.match(re('(?:' + PACKAGE_WORDS + ') ?of ' + N)))) {
      return counted(num(m[1]), '', v, '');
    }
    // 100 Sheets / 75 Hooks / 2 Pad
    if ((m = v.match(re(N + ' (sheets?|pieces?|pads?|books?|boxes|box|labels?|tags?|cards?|forms?|envelopes?|rolls?)')))) {
      return counted(num(m[1]), m[2], v, '');
    }
    // Pad 1 / Book 2 -> that many pads/books
    if ((m = v.match(re('(pad|book|pack) ' + N)))) {
      return counted(num(m[2]), m[1] === 'pack' ? '' : m[1], v, '');
    }
    // Amazon-style "12 roll (Pack of 2)" / "1 Count (Pack of 12)": clearly a
    // quantity, but the total is not stated, so no per-unit price.
    if (v.match(re(N + ' (?:count|ct|rolls?) \\(pack of ' + N + '\\)'))) {
      return { value: v, count: null, noun: '', heading: v, detail: '' };
    }
    // Bare numbers only when the option itself is a quantity (number_of_items: 2)
    if (/^\d[\d,]*$/.test(v) && /number|qty|quantity|count|items/i.test(optionName || '')) {
      return counted(num(v), '', v, '');
    }
    return null;
  }

  function prettyName(name) {
    var n = clean(name).replace(/_/g, ' ').toLowerCase();
    if (!n) return 'Option';
    return n.charAt(0).toUpperCase() + n.slice(1);
  }

  function sizedImage(url, width) {
    if (!url) return '';
    var u = String(url);
    if (u.indexOf('//') === 0) u = 'https:' + u;
    if (/[?&]width=\d+/.test(u)) return u.replace(/([?&])width=\d+/, '$1width=' + width);
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  var registry = [];

  function Panel(root) {
    this.root = root;
    this.state = 'starting';
  }

  Panel.prototype.fail = function (reason) {
    this.state = 'fallback';
    this.root.hidden = true;
    this.root.setAttribute('data-adh-state', 'fallback');
    this.root.setAttribute('data-adh-reason', reason);
    return false;
  };

  // Public fallback used by the refresh contract and by future theme integrations.
  // It deliberately restores the native path instead of attempting a partial panel.
  Panel.prototype.destroyToNativeFallback = function (reason) {
    var section = this.root.closest('.shopify-section') || this.root.parentNode;
    var result = this.fail(reason || 'manual-fallback');
    if (section && section.classList) section.classList.remove('adh-product-section');
    if (this.form && this.form.classList) this.form.classList.remove('adh-native-form');
    // Put back the theme's own button wording; a total left here would go stale once the quantity changes.
    // If the theme has rewritten the button since (e.g. "Out of stock"), its newer text stays.
    var label = this.form && this.form.querySelector('.t4s-btn-atc_text');
    if (label && this.panelAtcText != null && label.textContent === this.panelAtcText) label.textContent = this.nativeAtcText;
    this.panelAtcText = null;
    var anotherReady = registry.some(function (panel) { return panel !== this && panel.state === 'ready'; }, this);
    if (!anotherReady) document.documentElement.classList.remove('adh-panel-on');
    return result;
  };

  Panel.prototype.waitForTheme = function (container) {
    var self = this;
    var started = Date.now();
    this.state = 'waiting';
    var timer = window.setInterval(function () {
      if (container.classList.contains('initProducts__enabled')) {
        window.clearInterval(timer);
        try {
          self.init();
        } catch (e) {
          self.fail('error:' + (e && e.message ? e.message : 'unknown'));
        }
      } else if (Date.now() - started > 15000) {
        window.clearInterval(timer);
        self.fail('theme-not-ready');
      }
    }, 50);
    return false;
  };

  Panel.prototype.init = function () {
    var root = this.root;
    var data;
    try {
      data = JSON.parse(root.querySelector('[data-adh-bp-json]').textContent);
    } catch (e) {
      return this.fail('data-unreadable');
    }
    var form = document.getElementById(root.getAttribute('data-form-id'));
    if (!form) return this.fail('form-missing');
    var idField = form.querySelector('[name="id"]');
    if (!idField) return this.fail('variant-field-missing');
    if (!data.variants || !data.variants.length) return this.fail('no-variants');

    this.data = data;
    this.form = form;
    this.idField = idField;
    this.isSelect = idField.tagName === 'SELECT';
    this.money = new Intl.NumberFormat('en-US', { style: 'currency', currency: data.currency || 'USD' });
    this.variants = data.variants.map(function (v) {
      return {
        id: String(v.id), title: v.title, options: v.options || [], price: v.price,
        available: !!v.available, img: v.img || ''
      };
    });
    this.byId = {};
    for (var i = 0; i < this.variants.length; i++) this.byId[this.variants[i].id] = this.variants[i];

    if (this.isSelect) {
      // Every select option must be a known variant and every variant must be selectable.
      var opts = idField.options;
      if (opts.length !== this.variants.length) return this.fail('select-variant-count-mismatch');
      for (var j = 0; j < opts.length; j++) {
        if (!this.byId[opts[j].value]) return this.fail('select-variant-unknown');
      }
    } else if (this.variants.length !== 1 || !this.byId[idField.value]) {
      return this.fail('single-variant-mismatch');
    }

    this.options = data.defaultOnly ? [] : (data.options || []).map(function (o, index) {
      return { index: index, name: o.name, label: prettyName(o.name), values: o.values || [] };
    });
    // Option values used by variants must all be known option values.
    for (var k = 0; k < this.variants.length; k++) {
      var vv = this.variants[k];
      if (vv.options.length !== this.options.length && !data.defaultOnly) return this.fail('variant-options-mismatch');
      for (var q = 0; q < this.options.length; q++) {
        if (this.options[q].values.indexOf(vv.options[q]) === -1) return this.fail('variant-value-unknown');
      }
    }

    // Wait until the theme has wired its own variant handlers; until then its controls stay.
    var container = root.closest('[data-product-featured]');
    if (container && !container.classList.contains('initProducts__enabled')) return this.waitForTheme(container);

    this.classify();
    this.build();
    this.bind();
    root.hidden = false;
    var section = root.closest('.shopify-section') || root.parentNode;
    if (section && section.classList) section.classList.add('adh-product-section');
    form.classList.add('adh-native-form');
    // Lets the stylesheet recolour the theme's sticky bottom bar without needing :has().
    document.documentElement.classList.add('adh-panel-on');
    this.state = 'ready';
    root.setAttribute('data-adh-state', 'ready');
    root.setAttribute('data-adh-modes', this.options.map(function (o) { return o.mode; }).join(',') || 'single');
    this.sync();
    return true;
  };

  // Decide how each option is shown: quantity cards, photo cards, or text choices.
  Panel.prototype.classify = function () {
    var self = this;
    var photoCandidates = [];
    this.options.forEach(function (o) {
      if (o.values.length < 2) { o.mode = 'fixed'; return; }
      var parsed = o.values.map(function (val) { return parseQuantity(val, o.name); });
      var allQty = parsed.every(function (p) { return !!p; });
      if (allQty) {
        o.mode = 'quantity';
        o.qty = {};
        o.values.forEach(function (val, i) { o.qty[val] = parsed[i]; });
        var counts = parsed.map(function (p) { return p.count; });
        o.unitPricing = counts.every(function (c) { return typeof c === 'number' && c > 0; });
        if (o.unitPricing) {
          var nouns = parsed.map(function (p) { return p.noun || ''; });
          var same = nouns.every(function (n) { return n === nouns[0]; });
          o.unitNoun = same ? nouns[0] : '';
          o.unitBasis = Math.min.apply(null, counts) >= 100 ? 100 : 1;
        }
      } else {
        o.mode = 'choice';
      }
      // Photo evidence: each value's variants carry an image, and values look different.
      var reps = [];
      var consistent = 0;
      var everyValueHasImage = o.values.every(function (val) {
        var imgs = self.variants.filter(function (v) { return v.options[o.index] === val; }).map(function (v) { return v.img; });
        if (!imgs.length || imgs.some(function (src) { return !src; })) return false;
        reps.push(imgs[0]);
        var uniq = imgs.filter(function (src, i) { return imgs.indexOf(src) === i; });
        if (uniq.length === 1) consistent++;
        return true;
      });
      if (everyValueHasImage) {
        var distinct = reps.filter(function (src, i) { return reps.indexOf(src) === i; }).length;
        if (distinct >= 2 && distinct >= Math.ceil(o.values.length * 0.6)) {
          photoCandidates.push({ option: o, consistent: consistent / o.values.length, distinct: distinct });
        }
      }
    });
    // Photos go on one option only: the one whose values best determine the picture,
    // preferring a style/colour option over a quantity option.
    photoCandidates.sort(function (a, b) {
      if (b.consistent !== a.consistent) return b.consistent - a.consistent;
      var aq = a.option.mode === 'quantity' ? 1 : 0, bq = b.option.mode === 'quantity' ? 1 : 0;
      if (aq !== bq) return aq - bq;
      return b.distinct - a.distinct;
    });
    if (photoCandidates.length) photoCandidates[0].option.photos = true;
    this.options.forEach(function (o) {
      if (o.mode === 'choice' && o.photos) o.mode = 'photo';
    });
    // Unit counts are trusted only when a single option sets the quantity. With two
    // (e.g. "30 per tub" and "1 Count (Pack of 4)") neither count is the whole story.
    var qtyOptions = this.options.filter(function (o) { return o.mode === 'quantity'; });
    if (qtyOptions.length > 1 || UNITS_UNKNOWN[this.root.getAttribute('data-product-id') || '']) {
      qtyOptions.forEach(function (o) { o.unitPricing = false; o.unitsUnknown = true; });
    }
    // A bigger pack that works out dearer per item than a smaller one reads like a mistake to a buyer, so such
    // listings show no per-item price at all (Oct 5: snow rakes, hand-check items and others held from the price fix).
    qtyOptions.forEach(function (o) {
      if (o.unitPricing && self.ladderBackwards(o)) { o.unitPricing = false; o.unitHidden = true; }
    });
  };

  // True when, among variants that differ only in this quantity option, a bigger count shows a higher
  // per-item price (rounded as on the cards) than a smaller count.
  Panel.prototype.ladderBackwards = function (o) {
    var styles = {};
    this.variants.forEach(function (v) {
      var q = o.qty[v.options[o.index]];
      if (!q || !q.count) return;
      var key = JSON.stringify(v.options.filter(function (_, j) { return j !== o.index; }));
      (styles[key] = styles[key] || []).push({ count: q.count, unit: Math.round(v.price * o.unitBasis / q.count) });
    });
    return Object.keys(styles).some(function (k) {
      var ladder = styles[k].sort(function (a, b) { return a.count - b.count || a.unit - b.unit; });
      for (var i = 1; i < ladder.length; i++) {
        if (ladder[i].count !== ladder[i - 1].count && ladder[i].unit > ladder[i - 1].unit) return true;
      }
      return false;
    });
  };

  Panel.prototype.build = function () {
    var self = this;
    var root = this.root;
    var mount = root.querySelector('[data-adh-bp-mount]') || root;
    mount.textContent = '';

    var status = el('p', 'adh-bp__stock');
    status.setAttribute('data-adh-stock', '');
    mount.appendChild(status);
    this.stockEl = status;

    // Customer-specific prices belong to the wholesale app, not variant.price.
    // Keep pack selection without duplicating an unverified regular-price quote.
    if (this.data.customerPricing) {
      mount.appendChild(el('p', 'adh-bp__pricing-note', 'Dealer prices are supplied by your account pricing. Final pricing is confirmed at checkout.'));
    }

    this.groups = [];
    this.options.forEach(function (o) {
      if (o.mode === 'fixed') {
        var fixed = el('p', 'adh-bp__fixed');
        fixed.appendChild(el('span', 'adh-bp__fixed-name', o.label + ': '));
        fixed.appendChild(el('span', 'adh-bp__fixed-value', o.values[0] || ''));
        mount.appendChild(fixed);
        return;
      }
      var fs = el('fieldset', 'adh-bp__group adh-bp__group--' + o.mode);
      var legend = el('legend', 'adh-bp__legend');
      legend.appendChild(el('span', 'adh-bp__legend-name', o.label));
      var current = el('span', 'adh-bp__legend-value');
      legend.appendChild(current);
      fs.appendChild(legend);
      var list = el('div', 'adh-bp__list adh-bp__list--' + o.mode + (o.photos && o.mode === 'quantity' ? ' adh-bp__list--with-photos' : ''));
      if (o.mode === 'photo' && o.values.length > 8) list.className += ' adh-bp__list--compact';
      fs.appendChild(list);

      var buttons = o.values.map(function (val) {
        var b = el('button', 'adh-bp__choice adh-bp__choice--' + o.mode);
        b.type = 'button';
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('data-adh-option', String(o.index));
        b.setAttribute('data-adh-value', val);
        var parts = {};
        if (o.photos) {
          var img = el('img', 'adh-bp__img');
          img.alt = '';
          img.loading = 'lazy';
          img.width = 64; img.height = 64;
          b.appendChild(img);
          parts.img = img;
        }
        var copy = el('span', 'adh-bp__copy');
        if (o.mode === 'quantity') {
          var q = o.qty[val];
          var top = el('span', 'adh-bp__top');
          top.appendChild(el('span', 'adh-bp__title', q.heading));
          top.appendChild(el('span', 'adh-bp__radio'));
          top.lastChild.setAttribute('aria-hidden', 'true');
          copy.appendChild(top);
          if (q.detail) copy.appendChild(el('span', 'adh-bp__detail', q.detail));
          parts.price = el('span', 'adh-bp__price');
          copy.appendChild(parts.price);
          parts.unit = el('span', 'adh-bp__unit');
          parts.unit.setAttribute('data-adh-unit', '');
          copy.appendChild(parts.unit);
        } else {
          copy.appendChild(el('span', 'adh-bp__title', val));
          parts.price = el('span', 'adh-bp__price');
          copy.appendChild(parts.price);
        }
        parts.note = el('span', 'adh-bp__note');
        copy.appendChild(parts.note);
        b.appendChild(copy);
        list.appendChild(b);
        b.addEventListener('click', function () { self.choose(o.index, val); });
        return { value: val, button: b, parts: parts };
      });

      var toggle = null;
      var limit = o.mode === 'photo' ? 8 : 12;
      if (o.values.length > limit + 2) {
        toggle = el('button', 'adh-bp__more');
        toggle.type = 'button';
        toggle.setAttribute('aria-expanded', 'false');
        fs.appendChild(toggle);
        toggle.addEventListener('click', function () {
          var open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', String(open));
          self.sync();
        });
      }
      self.groups.push({ option: o, fieldset: fs, current: current, buttons: buttons, toggle: toggle, limit: limit });
      mount.appendChild(fs);
    });

    var summary = el('p', 'adh-bp__summary');
    summary.setAttribute('aria-live', 'polite');
    summary.setAttribute('data-adh-summary', '');
    mount.appendChild(summary);
    this.summaryEl = summary;
  };

  Panel.prototype.bind = function () {
    var self = this;
    var form = this.form;
    var later = function () { window.setTimeout(function () { self.sync(); }, 0); };
    this.idField.addEventListener('change', later);
    form.addEventListener('input', later);
    form.addEventListener('change', later);
    form.addEventListener('click', later);
    var jq = window.jQuery_T4NT || window.jQuery;
    if (jq && jq.fn && jq.fn.on) {
      try {
        jq(document).on('variant:changed', later);
        // The theme's +/- buttons and the sticky bar change the quantity with jQuery events only.
        jq(document).on('change', '[data-quantity-value]', later);
      } catch (e) { /* theme events optional */ }
    }
    var qty = form.querySelector('input[name="quantity"]');
    if (qty && !qty.getAttribute('aria-label')) qty.setAttribute('aria-label', 'Quantity');
    var minus = form.querySelector('[data-decrease-qty]');
    var plus = form.querySelector('[data-increase-qty]');
    if (minus && !minus.getAttribute('aria-label')) minus.setAttribute('aria-label', 'Decrease quantity');
    if (plus && !plus.getAttribute('aria-label')) plus.setAttribute('aria-label', 'Increase quantity');
    this.guardSoldOut();
  };

  // The theme marks a sold-out Add to Cart only with aria-disabled, so on phones a tap still
  // submitted the form and Shopify put the sold-out item in the cart (found Oct 5; today's
  // live theme does the same). Stop every add of a sold-out choice: the main button, a form
  // submit, and the theme's sticky bar, which adds by its own data-variant-id.
  Panel.prototype.guardSoldOut = function () {
    var self = this;
    var soldOut = function (id) { var v = self.byId[String(id)]; return !!v && !v.available; };
    var block = function (e) { e.preventDefault(); e.stopImmediatePropagation(); };
    this.form.addEventListener('submit', function (e) {
      if (soldOut(self.idField.value)) block(e);
    }, true);
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var add = t.closest('button[name="add"]');
      if (add && add.form === self.form && soldOut(self.idField.value)) { block(e); return; }
      var sticky = t.closest('[data-action-atc]');
      if (sticky && soldOut(sticky.getAttribute('data-variant-id'))) block(e);
    }, true);
  };

  Panel.prototype.current = function () {
    return this.byId[String(this.idField.value)] || null;
  };

  Panel.prototype.selectable = function (v) {
    // Mirrors the live theme: when the product has stock, sold-out variants are not selectable.
    if (!v) return false;
    if (this.data.removeSoldout && this.data.available) return v.available;
    return true;
  };

  Panel.prototype.exact = function (optionIndex, value) {
    var cur = this.current();
    if (!cur) return null;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      var ok = true;
      for (var k = 0; k < this.options.length; k++) {
        var want = k === optionIndex ? value : cur.options[k];
        if (v.options[k] !== want) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  // Variant to select when a value is chosen: the exact combination when allowed,
  // otherwise the closest selectable variant that has the chosen value.
  Panel.prototype.target = function (optionIndex, value) {
    var exact = this.exact(optionIndex, value);
    if (exact && this.selectable(exact)) return exact;
    var cur = this.current();
    var best = null, bestScore = -1;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      if (v.options[optionIndex] !== value || !this.selectable(v)) continue;
      var score = 0;
      for (var k = 0; k < this.options.length; k++) {
        if (k !== optionIndex && cur && v.options[k] === cur.options[k]) score++;
      }
      score = score * 2 + (v.available ? 1 : 0);
      if (score > bestScore) { best = v; bestScore = score; }
    }
    return best;
  };

  Panel.prototype.choose = function (optionIndex, value) {
    var v = this.target(optionIndex, value);
    if (!v) return;
    this.selectVariant(v);
  };

  // Hands the choice to the theme. The sticky bar's "change:drop" route is used when it
  // exists because it also clears the theme's leftover swatch-click flag; without that, an
  // earlier tap on the theme's own swatches leaves its price, SKU and URL on the old variant.
  Panel.prototype.selectVariant = function (v) {
    if (typeof v !== 'object') v = this.byId[String(v)];
    if (!v) return false;
    var field = this.idField;
    if (String(field.value) === v.id) { this.sync(); return; }
    if (this.isSelect) {
      var option = null;
      for (var i = 0; i < field.options.length; i++) if (field.options[i].value === v.id) option = field.options[i];
      // A disabled option is never submitted with the form, and the theme errors on it.
      if (!option || option.disabled) return;
      var jq = window.jQuery_T4NT;
      var container = this.root.closest('[data-product-featured]');
      var seen = null;
      var spy = function (e) { seen = e && e.currentVariant ? String(e.currentVariant.id) : null; };
      if (jq && container) jq(container).on('variant:changed', spy);
      try {
        var drop = document.querySelector('[data-sticky-addtocart] [data-sticky-select]');
        if (jq && drop) {
          jq(drop).trigger('change:drop', [null, v.id]);
        }
        if (String(field.value) !== v.id) {
          field.value = v.id;
          if (field.value !== v.id) option.selected = true;
          field.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
        }
        if (seen !== null && seen !== v.id) this.repairTheme(v);
      } finally {
        if (jq && container) jq(container).off('variant:changed', spy);
      }
    }
    this.sync();
    return true;
  };

  Panel.prototype.syncQuantity = function (value) {
    var input = this.form && this.form.querySelector('input[name="quantity"]');
    var qty = Number(value);
    if (!input || !Number.isFinite(qty) || qty < 1 || Math.floor(qty) !== qty) return false;
    input.value = String(qty);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    this.sync();
    return true;
  };

  // Fallback when the theme reported a different variant than the one chosen (no sticky bar
  // to clear its flag): repeat the choice through the theme's own swatches, which it trusts.
  Panel.prototype.repairTheme = function (v) {
    var form = this.form;
    var field = this.idField;
    var options = this.options;
    // Two passes: a click on one option can make the theme move another option.
    // (The select already holds v here; it is the theme's display that lags, so always click.)
    for (var pass = 0; pass < 2; pass++) {
      options.forEach(function (o) {
        var group = form.querySelector('[data-swatch-option][data-id="' + o.index + '"]');
        if (!group) return;
        var items = group.querySelectorAll('[data-swatch-item]');
        for (var i = 0; i < items.length; i++) {
          if (items[i].getAttribute('data-value') === v.options[o.index]) {
            if (!items[i].classList.contains('is--selected')) items[i].click();
            break;
          }
        }
      });
    }
    if (String(this.idField.value) !== v.id) {
      this.idField.value = v.id;
      this.idField.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
    }
  };

  Panel.prototype.quantity = function () {
    var input = this.form.querySelector('input[name="quantity"]');
    var n = input ? Number(input.value) : 1;
    return Number.isFinite(n) && n >= 1 && Math.floor(n) === n ? n : null;
  };

  Panel.prototype.unitText = function (o, q, price) {
    if (!o.unitPricing || !q || !q.count) return '';
    var basis = o.unitBasis;
    var cents = Math.round(price * basis / q.count);
    var noun = o.unitNoun;
    if (basis === 1) return this.money.format(cents / 100) + (noun ? ' per ' + singular(noun) : ' each');
    return this.money.format(cents / 100) + ' per ' + fmt(basis) + (noun ? ' ' + plural(noun) : '');
  };

  // Use Massy's own custom element: it owns eligibility, tiers and rounding.
  // Observe only its rendered output; never infer a percentage from another pack.
  Panel.prototype.syncAppPrice = function (parts, variant, qty) {
    var self = this;
    if (!parts.appPrice) {
      parts.price.textContent = '';
      parts.appPrice = el('massy-custom-pricing', 'adh-bp__account-price');
      parts.pendingPrice = el('span', '', 'Loading account price…');
      parts.price.appendChild(parts.appPrice);
      parts.price.appendChild(parts.pendingPrice);
      parts.price.hidden = false;
      parts.appObserver = new MutationObserver(function () {
        var node = parts.appPrice.querySelector('.massy-custom-pricing-price-discount, .massy-custom-pricing-price-regular');
        var match = node && clean(node.textContent).match(/^\$([\d,]+\.\d{2})$/);
        var cents = self.data.currency === 'USD' && match ? Math.round(Number(match[1].replace(/,/g, '')) * 100) : null;
        parts.accountCents = Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
        parts.quoteReady = !!node && !!clean(node.textContent);
        parts.appPrice.hidden = !parts.quoteReady;
        parts.pendingPrice.hidden = parts.quoteReady;
        self.sync();
      });
      parts.appObserver.observe(parts.appPrice, { childList: true, characterData: true, subtree: true });
    }
    var request = variant && qty ? {
      variantId: variant.id, productId: String(this.data.id || this.root.getAttribute('data-product-id')),
      productHandle: this.data.handle, quantity: qty, inCart: false,
      showUnitPrice: true, showUnitPriceMeasurement: false, showBadge: false, showTimer: false, priceDisplay: 0
    } : null;
    var key = JSON.stringify(request);
    if (parts.accountKey !== key) {
      parts.accountKey = key;
      parts.accountRequest = request;
      parts.accountCents = null;
      parts.quoteReady = false;
      parts.appPrice.hidden = true;
      parts.pendingPrice.hidden = false;
      if (parts.unit) parts.unit.textContent = '';
      // A fresh element prevents an old async render being mistaken for the new quantity.
      // Massy's disconnected callback releases its subscriptions.
      var fresh = el('massy-custom-pricing', 'adh-bp__account-price');
      fresh.hidden = true;
      if (request) fresh.setAttribute('massy-data', key);
      parts.appObserver.disconnect();
      parts.appPrice.replaceWith(fresh);
      parts.appPrice = fresh;
      parts.appObserver.observe(fresh, { childList: true, characterData: true, subtree: true });
    }
    if (parts.unit) parts.unit.hidden = false;
  };

  Panel.prototype.currentAccountPrice = function (cur, qty) {
    var result = null;
    this.groups.forEach(function (g) { g.buttons.forEach(function (item) {
      var p = item.parts, req = p.accountRequest;
      if (req && req.variantId === cur.id && req.quantity === qty && p.quoteReady && p.accountCents != null) result = p.accountCents;
    }); });
    return result;
  };

  Panel.prototype.sync = function () {
    if (this.state !== 'ready') return;
    var self = this;
    var cur = this.current();
    if (!cur) return;
    var qty = this.quantity();

    this.stockEl.textContent = cur.available ? 'In stock' : 'Sold out';
    this.stockEl.className = 'adh-bp__stock ' + (cur.available ? 'is-in' : 'is-out');

    this.groups.forEach(function (g) {
      var o = g.option;
      g.current.textContent = cur.options[o.index] || '';
      var prices = g.buttons.map(function (item) {
        var ex = self.exact(o.index, item.value);
        return ex ? ex.price : null;
      });
      var known = prices.filter(function (p) { return p != null; });
      var varies = known.some(function (p) { return p !== known[0]; });
      var expanded = !g.toggle || g.toggle.getAttribute('aria-expanded') === 'true';
      g.buttons.forEach(function (item, idx) {
        var b = item.button, parts = item.parts;
        var selected = cur.options[o.index] === item.value;
        var exact = self.exact(o.index, item.value);
        var target = self.target(o.index, item.value);
        b.setAttribute('aria-pressed', String(selected));
        var blocked = !target;
        if (blocked) b.setAttribute('aria-disabled', 'true'); else b.removeAttribute('aria-disabled');
        b.classList.toggle('is-soldout', blocked || !!(exact && !exact.available));
        var note = '';
        if (blocked) note = 'Sold out';
        else if (!exact || !self.selectable(exact)) note = exact && !exact.available ? 'Sold out in this combination' : 'Available with other options';
        else if (!exact.available) note = 'Sold out';
        else if (selected && o.mode === 'photo') note = 'Selected';
        parts.note.textContent = note;
        var shown = exact && self.selectable(exact) ? exact : null;
        if (parts.img) {
          var src = sizedImage((shown || target || exact || {}).img || '', 160);
          if (src && parts.img.getAttribute('src') !== src) parts.img.setAttribute('src', src);
        }
        if (self.data.customerPricing) {
          self.syncAppPrice(parts, shown, qty);
          if (parts.unit) parts.unit.textContent = parts.quoteReady && parts.accountCents != null ? self.unitText(o, o.qty[item.value], parts.accountCents) : '';
        } else if (o.mode === 'quantity') {
          parts.price.textContent = shown ? self.money.format(shown.price / 100) : '';
          parts.unit.textContent = shown ? self.unitText(o, o.qty[item.value], shown.price) : '';
        } else {
          parts.price.textContent = shown && varies ? self.money.format(shown.price / 100) : '';
        }
        b.hidden = !expanded && idx >= g.limit && !selected;
      });
      if (g.toggle) {
        var open = g.toggle.getAttribute('aria-expanded') === 'true';
        g.toggle.textContent = open ? 'Show fewer' : 'Show all ' + o.values.length + ' choices';
      }
    });

    this.summaryEl.textContent = this.summary(cur, qty);
    this.summaryEl.hidden = !this.summaryEl.textContent;

    var label = this.form.querySelector('.t4s-btn-atc_text');
    var button = this.form.querySelector('button[name="add"]');
    if (label && button && cur.available && !button.disabled) {
      // Remember the theme's wording whenever the theme (not the panel) last set it, so a fallback can restore it.
      if (label.textContent !== this.panelAtcText) this.nativeAtcText = label.textContent;
      // No total while the quantity box is empty or not a whole number.
      var quotedPrice = this.data.customerPricing ? this.currentAccountPrice(cur, qty) : cur.price;
      label.textContent = qty && quotedPrice != null ? 'Add to Cart · ' + this.money.format(qty * quotedPrice / 100) : 'Add to Cart';
      this.panelAtcText = label.textContent;
    }
  };

  Panel.prototype.summary = function (cur, qty) {
    var self = this;
    if (!qty) return 'Enter a whole number for quantity.';
    var qtyOption = null;
    var others = [];
    this.options.forEach(function (o) {
      if (o.mode === 'quantity' && !qtyOption) qtyOption = o;
      else if (o.values.length > 1) others.push(cur.options[o.index]);
    });
    var lead = others.length ? others.join(' · ') + ' · ' : '';
    if (qtyOption && qtyOption.unitsUnknown) {
      var named = this.options.filter(function (o) { return o.values.length > 1; }).map(function (o) { return cur.options[o.index]; });
      return fmt(qty) + ' × ' + (named.length ? named.join(' · ') : cur.title);
    }
    if (qtyOption) {
      var q = qtyOption.qty[cur.options[qtyOption.index]];
      if (q && q.packages) {
        var packs = qty * q.packages;
        var nounPer = q.noun ? ' ' + nounFor(q.noun, q.per) : '';
        var nounTot = q.noun ? ' ' + nounFor(q.noun, qty * q.count) : '';
        return lead + fmt(packs) + ' ' + nounFor(q.pkg, packs) + ' × ' + fmt(q.per) + nounPer + ' = ' + fmt(qty * q.count) + nounTot + ' total';
      }
      if (q && q.count) {
        var tail = q.noun ? ' ' + nounFor(q.noun, qty * q.count) : ' total';
        return lead + fmt(qty) + ' × ' + q.value + ' = ' + fmt(qty * q.count) + tail;
      }
      return lead + fmt(qty) + ' × ' + (q ? q.value : cur.title);
    }
    if (!this.options.length) return '';
    return lead.replace(/ · $/, '') + (lead ? ' · ' : '') + 'Quantity ' + fmt(qty);
  };

  function mountPanel(root) {
    if (!root) return null;
    if (root.__adhPanel) return root.__adhPanel;
    var panel = new Panel(root);
    root.__adhPanel = panel;
    try {
      panel.init();
    } catch (e) {
      panel.fail('error:' + (e && e.message ? e.message : 'unknown'));
      if (window.console && console.warn) console.warn('ADH buying panel fell back to theme controls', e);
    }
    registry.push(panel);
    return panel;
  }

  function scan() {
    var roots = document.querySelectorAll('[data-adh-bp]');
    for (var i = 0; i < roots.length; i++) {
      mountPanel(roots[i]);
    }
  }

  window.ADHBuyingPanel = {
    version: VERSION,
    parseQuantity: parseQuantity,
    mountPanel: mountPanel,
    panels: registry,
    scan: scan
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
  document.addEventListener('shopify:section:load', scan);
})();
